var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import FetchHelper from "./fetchHelper.js";
/**
 * Browser-side web push for the member site: service worker registration, subscribing and
 * unsubscribing this device, and keeping the server's copy of the subscription fresh.
 */
const SERVICE_WORKER_URL = "/sw.js";
const RESYNC_SESSION_KEY = "bp-push-resynced";
export const pushSupported = () => typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
/** iOS only offers web push to a site added to the home screen (iOS 16.4+). */
export const isIosOutsideHomeScreen = () => {
    var _a;
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent)
        || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const standalone = ((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, "(display-mode: standalone)").matches)
        || navigator.standalone === true;
    return ios && !standalone;
};
export const registerServiceWorker = () => {
    if (!("serviceWorker" in navigator))
        return Promise.resolve(undefined);
    return navigator.serviceWorker.register(SERVICE_WORKER_URL).catch((error) => {
        console.error("[BioIntra] service worker registration failed", error);
        return undefined;
    });
};
const currentSubscription = () => __awaiter(void 0, void 0, void 0, function* () {
    if (!pushSupported())
        return null;
    const registration = yield navigator.serviceWorker.ready;
    return registration.pushManager.getSubscription();
});
export const getDeviceState = () => __awaiter(void 0, void 0, void 0, function* () {
    if (!pushSupported())
        return "unsupported";
    if (Notification.permission === "denied")
        return "blocked";
    return (yield currentSubscription()) ? "on" : "off";
});
const urlBase64ToUint8Array = (base64) => {
    const padding = "=".repeat((4 - (base64.length % 4)) % 4);
    const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
    const bytes = new Uint8Array(new ArrayBuffer(raw.length));
    for (let i = 0; i < raw.length; i++)
        bytes[i] = raw.charCodeAt(i);
    return bytes;
};
/** Asks for permission, subscribes this device and tells the server. Returns the new state. */
export const enablePush = () => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    if (!pushSupported())
        return "unsupported";
    const permission = yield Notification.requestPermission();
    if (permission !== "granted")
        return permission === "denied" ? "blocked" : "off";
    const { publicKey } = (yield FetchHelper.get("/bpapi/pushsubscription/publickey"));
    if (!publicKey)
        throw new Error("Push is not configured on the server");
    const registration = (_a = (yield registerServiceWorker())) !== null && _a !== void 0 ? _a : (yield navigator.serviceWorker.ready);
    yield navigator.serviceWorker.ready;
    const subscription = (_b = (yield registration.pushManager.getSubscription())) !== null && _b !== void 0 ? _b : yield registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
    });
    yield FetchHelper.post("/bpapi/pushsubscription", subscription.toJSON());
    return "on";
});
/** Unsubscribes this device and tells the server. Logging out deliberately does not do this. */
export const disablePush = () => __awaiter(void 0, void 0, void 0, function* () {
    const subscription = yield currentSubscription();
    if (subscription) {
        yield FetchHelper.post("/bpapi/pushsubscription/unsubscribe", { endpoint: subscription.endpoint });
        yield subscription.unsubscribe();
    }
    return pushSupported() ? "off" : "unsupported";
});
/**
 * Re-sends this device's subscription for the logged-in member once per browser session, and again
 * whenever a different member logs in. That keeps the server's row from being purged as stale,
 * repairs it if the browser rotated the endpoint, and moves the browser to whoever now uses it -
 * otherwise a member switch in the same session would leave the device receiving the previous
 * member's notifications.
 */
export const resyncSubscription = (memberKey) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        if (!memberKey || !pushSupported() || Notification.permission !== "granted")
            return;
        if (sessionStorage.getItem(RESYNC_SESSION_KEY) === memberKey)
            return;
        const subscription = yield currentSubscription();
        if (!subscription)
            return;
        yield FetchHelper.post("/bpapi/pushsubscription", subscription.toJSON());
        sessionStorage.setItem(RESYNC_SESSION_KEY, memberKey);
    }
    catch (_a) {
        // Best effort; the next visit tries again.
    }
});
/** Sets, or clears at zero, the home-screen icon badge where the browser supports it. */
export const setAppBadge = (count) => {
    var _a, _b, _c, _d, _e, _f;
    const nav = navigator;
    if (count > 0)
        (_c = (_b = (_a = nav.setAppBadge) === null || _a === void 0 ? void 0 : _a.call(nav, count)) === null || _b === void 0 ? void 0 : _b.catch) === null || _c === void 0 ? void 0 : _c.call(_b, () => undefined);
    else
        (_f = (_e = (_d = nav.clearAppBadge) === null || _d === void 0 ? void 0 : _d.call(nav)) === null || _e === void 0 ? void 0 : _e.catch) === null || _f === void 0 ? void 0 : _f.call(_e, () => undefined);
};
